import { useCallback, useEffect, useRef } from 'react'

import { Box, Skeleton } from '@chakra-ui/react'

import Card from '@/views/components/Card'

import { useStickyVideoPlayerCollapseAnimation } from './useStickyVideoPlayerCollapseAnimation'

interface VideoPlayerProps {
  videoUrl: string | null
  isLoading?: boolean
  onPlayerReady?: (seekTo: (timeInSeconds: number) => void) => void
}

export const VideoPlayer = ({
  onPlayerReady,
  videoUrl,
  isLoading = false,
}: VideoPlayerProps) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const sentinelRef = useRef<HTMLDivElement>(null)

  const stickyRootRef = useRef<HTMLDivElement>(null)
  const animatedContainerRef = useRef<HTMLDivElement>(null)

  useStickyVideoPlayerCollapseAnimation({
    sentinelRef,
    stickyRootRef,
    animatedContainerRef,
    collapsedMaxWidthPx: 500,
    collapsedPaddingPx: 8,
    collapseDistancePx: 240,
    smoothingFactor: 0.22,
  })

  const seekTo = useCallback((timeInSeconds: number) => {
    const video = videoRef.current
    if (!video) return

    video.currentTime = timeInSeconds
    video.play().catch(() => {
      // Ignore play errors (e.g., user hasn't interacted with page yet)
    })
  }, [])

  useEffect(() => {
    onPlayerReady?.(seekTo)
  }, [onPlayerReady, seekTo])

  const content = isLoading ? (
    <Box
      width="100%"
      style={{
        aspectRatio: '16 / 9',
      }}
    >
      <Skeleton
        height="100%"
        width="100%"
        borderRadius="0"
        startColor="#f5f5f5"
        endColor="#d7d7d7"
      />
    </Box>
  ) : !videoUrl ? null : (
    <Box
      as="video"
      ref={videoRef}
      controls
      className="w-full"
      src={videoUrl}
      style={{
        aspectRatio: '16 / 9',
        backgroundColor: '#000',
        display: 'block',
      }}
    />
  )

  if (!content) return null

  return (
    <>
      <div ref={sentinelRef} className="h-0" />

      <div
        ref={stickyRootRef}
        className="sticky top-0 z-10 flex justify-center"
        style={{
          backgroundColor:
            'rgba(0, 0, 0, var(--video-player-background-alpha, 0))',
          overflowAnchor: 'none',
        }}
      >
        <div
          ref={animatedContainerRef}
          className="w-full"
          style={{
            width: 'var(--video-player-container-width, 100%)',
            padding: 'var(--video-player-container-padding, 0px)',
            maxWidth: '100%',
            boxSizing: 'border-box',
            willChange: 'width, padding',
          }}
        >
          <Card className="overflow-hidden">{content}</Card>
        </div>
      </div>

      <div className="mb-5" />
    </>
  )
}
